import Modal from './modal.js';
import countries from 'i18n-iso-countries';
import enCountryLocale from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(enCountryLocale);

const SELECTORS = {
    CONTENT_TEMPLATE: '[data-content-template]',
    COUNTRY_WITH_FLAG_TEMPLATE: '[data-country-with-flag-template]',
    OPTION_DATA: '[data-redirect-option-data]',
    ACCEPT_BUTTON: '[data-geo-accept]'
};

class GeolocationModal extends Modal {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        this.init();
    }

    init() {
        console.debug('[GeolocationModal] init');

        if (sessionStorage.getItem('geolocation-modal-closed')) {
            console.debug('[GeolocationModal] init: geolocation-modal-closed');
            return;
        }

        this.getGeolocationOptionData();

        this.shopCountryRedirectOption = this.getRedirectOption(window.Shopify.country);

        document.addEventListener(
            'geolocation-recommendation',
            this.handleGeolocationRecommendation.bind(this)
        );
    }

    getGeolocationOptionData() {
        console.debug('[GeolocationModal] getGeolocationOptionData');

        try {
            const redirectOptionData = this.querySelector(SELECTORS.OPTION_DATA);
            if (!redirectOptionData) {
                throw new Error(
                    `Missing geolocation data script (${SELECTORS.OPTION_DATA}) in geoloaction modal`
                );
            }

            const json = JSON.parse(redirectOptionData.textContent);

            this.redirectOptions = json.redirect_options;

            console.debug('[GeolocationModal] this.redirectOptions', this.redirectOptions);

            if (!this.redirectOptions) {
                throw new Error('Missing redirect options in geoloaction data');
            }

            redirectOptionData.remove();
        } catch (error) {
            throw new Error(`Error getting geoloaction data: ${error}`, this);
        }
    }

    getRedirectOption(country) {
        const getPotentialRedirect = (redirectOptions) => {
            console.debug('[GeolocationModal] getRedirectOption', country);
            console.debug('[GeolocationModal] redirectOptions', redirectOptions);
            const redirectOptionByCountry = redirectOptions.find((redirectOption) =>
                redirectOption.countries.includes(country)
            );

            if (redirectOptionByCountry) {
                return redirectOptionByCountry;
            }

            return redirectOptions.find((redirectOption) => redirectOption.is_default);
        };

        const redirectOption = getPotentialRedirect(this.redirectOptions);

        if (this.shopCountryRedirectOption && this.shopCountryRedirectOption.name === redirectOption.name) {
            return null;
        }

        return redirectOption;
    }

    populateTemplate({ customerCountry, shopCountry, redirectOption }) {
        if (!this.contentTemplate) {
            this.contentTemplate = this.querySelector(SELECTORS.CONTENT_TEMPLATE);
            if (!this.contentTemplate) {
                throw new Error(
                    `Missing content template (${SELECTORS.CONTENT_TEMPLATE}s) in geoloaction modal`
                );
            }
        }

        const contentString = this.contentTemplate.innerHTML
            .replaceAll('[customer_country]', customerCountry)
            .replaceAll('[customer_country_w_flag]', this.getCustomerCountryWithFlag(customerCountry, this.recommendedCountry))
            .replaceAll('[shop_country]', shopCountry)
            .replaceAll('[shop_country_w_flag]', this.getCountryHTMlWithFlag(this.shopCountryRedirectOption))
            .replaceAll('[redirect_name]', redirectOption.name)
            .replaceAll('[redirect_short_name]', redirectOption.short_name)
            .replaceAll('[redirect_name_w_flag]', this.getCountryHTMlWithFlag(redirectOption));

        console.debug('[GeolocationModal] contentString', contentString);

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = contentString;
        this.contentTemplate.parentNode.insertBefore(tempDiv.firstElementChild, this.contentTemplate);

        console.debug('[GeolocationModal] tempDiv', tempDiv);

        this.contentTemplate.remove();

        // Replace variables in button text
        this.replaceVariablesInButtons({ customerCountry, shopCountry, redirectOption });
    }

    getCountryWithFlagHTML(imageUrl, name) {
        if (!this.countryWithFlagTemplate) {
            this.countryWithFlagTemplate = this.querySelector(SELECTORS.COUNTRY_WITH_FLAG_TEMPLATE);
            if (!this.countryWithFlagTemplate) {
                throw new Error(
                    `Missing country with flag template (${SELECTORS.COUNTRY_WITH_FLAG_TEMPLATE}s) in geoloaction modal`
                );
            }
        }

        const countryWithFlagTemplate = this.countryWithFlagTemplate.innerHTML
            .replaceAll('[image_url]', imageUrl)
            .replaceAll('[name]', name);

        this.countryWithFlagTemplate.remove();

        return countryWithFlagTemplate;
    }

    getCountryHTMlWithFlag(redirectOption) {
        return this.getCountryWithFlagHTML(redirectOption.image_url, redirectOption.name);
    }

    getCustomerCountryWithFlag(customerCountry, isoCode) {
        return this.getCountryWithFlagHTML(this.getCountryFlagUrlFromISO(isoCode), customerCountry);
    }

    replaceVariablesInButtons({ customerCountry, shopCountry, redirectOption }) {
        const acceptButton = this.querySelector(SELECTORS.ACCEPT_BUTTON);
        const declineButton = this.querySelector('[data-toggle-geolocation-modal]');

        if (acceptButton) {
            acceptButton.innerHTML = acceptButton.innerHTML
                .replaceAll('[customer_country]', customerCountry)
                .replaceAll('[customer_country_w_flag]', this.getCustomerCountryWithFlag(customerCountry, this.recommendedCountry))
                .replaceAll('[shop_country]', shopCountry)
                .replaceAll(
                    '[shop_country_w_flag]',
                    this.getCountryHTMlWithFlag(this.shopCountryRedirectOption)
                )
                .replaceAll('[redirect_name]', redirectOption.name)
                .replaceAll('[redirect_short_name]', redirectOption.short_name)
                .replaceAll('[redirect_name_w_flag]', this.getCountryHTMlWithFlag(redirectOption));
        }

        if (declineButton) {
            declineButton.innerHTML = declineButton.innerHTML
                .replaceAll('[customer_country]', customerCountry)
                .replaceAll('[customer_country_w_flag]', this.getCustomerCountryWithFlag(customerCountry, this.recommendedCountry))
                .replaceAll('[shop_country]', shopCountry)
                .replaceAll(
                    '[shop_country_w_flag]',
                    this.getCountryHTMlWithFlag(this.shopCountryRedirectOption)
                )
                .replaceAll('[redirect_name]', redirectOption.name)
                .replaceAll('[redirect_short_name]', redirectOption.short_name)
                .replaceAll('[redirect_name_w_flag]', this.getCountryHTMlWithFlag(redirectOption));
        }
    }

    getCountryFromISO(isoCode) {
        if (!isoCode) {
            throw new Error(`Invalid ISO code: ${isoCode}`);
        }

        const countryName = countries.getName(isoCode, 'en');
        console.debug('[GeolocationModal] countryName', countryName);
        switch (countryName) {
            case 'United States of America':
                return 'United States';
            default:
                return countryName;
        }
    }

    getCountryFlagUrlFromISO(isoCode) {
        return `https://flagcdn.com/w160/${isoCode.toLowerCase()}.png`;
    }

    handleGeolocationRecommendation(event) {
        console.debug('[GeolocationModal] handleGeolocationRecommendation', event);

        this.recommendedCountry = event.detail.recommendedCountry;

        const redirectOption = this.getRedirectOption(this.recommendedCountry);
        console.debug('[GeolocationModal] redirectOption', redirectOption);
        if (redirectOption) {
            this.populateTemplate({
                customerCountry: this.getCountryFromISO(this.recommendedCountry),
                shopCountry: this.shopCountryRedirectOption.name,
                redirectOption: redirectOption
            });

            console.debug('[GeolocationModal] recommendedCountry', this.recommendedCountry);

            this.delegateClick(SELECTORS.ACCEPT_BUTTON, (match, event) => {
                this.handleAcceptButtonClick({
                    redirectOption
                });
            });

            this.open();
            console.debug('[GeolocationModal] this', this);
        }
    }

    handleAcceptButtonClick({ redirectOption }) {
        try {
            console.debug('[GeolocationModal] handleAcceptButtonClick', redirectOption);
            const currentPath = this.getCurrentPath();
            const redirectUrl = new URL(redirectOption.redirect_url);
            if (this.recommendedCountry) {
                redirectUrl.searchParams.set('country', this.recommendedCountry);
            }

            // Combine the redirect URL path with the current path
            const basePath = redirectUrl.pathname === '/' ? '' : redirectUrl.pathname;
            redirectUrl.pathname = basePath + currentPath;

            window.location.href = redirectUrl.toString();
        } catch (error) {
            console.error('[GeolocationModal] handleAcceptButtonClick', error);
            window.location.href = redirectOption.redirect_url;
        }
    }

    getCurrentPath() {
        const rootpath = window.Shopify.routes.root;
        const pathname = window.location.pathname.replace(rootpath, '');
        const search = window.location.search;

        return pathname + search;
    }

    onClose() {
        sessionStorage.setItem('geolocation-modal-closed', 'true');
    }

    onThemeEditorLoad() {
        const redirectOption = this.redirectOptions.find((redirectOption) => redirectOption.is_default);
        this.recommendedCountry = 'LT'; // Set for theme editor preview
        this.populateTemplate({
            customerCountry: this.getCountryFromISO('LT'),
            shopCountry: this.shopCountryRedirectOption.name,
            redirectOption: redirectOption
        });
    }
}

customElements.define('geolocation-modal', GeolocationModal);
